import { useInfiniteUserPosts } from '../../hooks/usePosts';
import PostCard from './PostCard';
import { useEffect, useRef } from 'react';

export default function MyPostsSection() {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteUserPosts();

  const posts = data?.pages.flat() || [];
  const observerTarget = useRef<HTMLDivElement>(null);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="theme-bg-primary rounded-lg shadow-sm border theme-border-primary p-6">
          <h2 className="text-xl font-semibold theme-text-primary mb-4">My Posts</h2>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-healthy-green mx-auto"></div>
            <p className="mt-2 theme-text-secondary">Loading your posts...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="theme-bg-primary rounded-lg shadow-sm border theme-border-primary p-6">
          <h2 className="text-xl font-semibold theme-text-primary mb-4">My Posts</h2>
          <div className="text-center py-8">
            <div className="text-red-500 mb-2">
              <svg className="h-8 w-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="theme-text-secondary mb-4">Failed to load your posts</p>
            <p className="text-sm theme-text-tertiary">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="space-y-4">
        <div className="theme-bg-primary rounded-lg shadow-sm border theme-border-primary p-6">
          <h2 className="text-xl font-semibold theme-text-primary mb-4">My Posts</h2>
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-healthy-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <p className="theme-text-secondary mb-2">You haven't created any posts yet</p>
            <p className="text-sm theme-text-tertiary">Your posts will appear here once you start sharing</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="theme-bg-primary rounded-lg shadow-sm border theme-border-primary p-4">
        <h2 className="text-xl font-semibold theme-text-primary">
          My Posts ({posts.length}{hasNextPage ? '+' : ''})
        </h2>
      </div>
      {posts.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}

      {/* Infinite scroll trigger */}
      <div ref={observerTarget} className="h-4" />

      {/* Loading indicator */}
      {isFetchingNextPage && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-healthy-green mx-auto"></div>
          <p className="mt-2 text-sm theme-text-secondary">Loading more posts...</p>
        </div>
      )}

      {/* End of list indicator */}
      {!hasNextPage && posts.length > 0 && (
        <div className="text-center py-4">
          <p className="text-sm theme-text-tertiary">You've reached the end</p>
        </div>
      )}
    </div>
  );
}
